import assert from 'assert';
import axios, { AxiosError } from 'axios';
import TokenRefreshError from 'errors/TokenRefreshError';
import { getLocale } from 'locales/utils';
import { AuthService } from 'services/AuthService';

const version = process.env.VERSION;
const oauthClientId = process.env.OAUTH_CLIENT_ID;
assert(version, 'process.env.VERSION not found');
assert(oauthClientId, 'process.env.OAUTH_CLIENT_ID not found');

const commonConfig = {
  baseURL: process.env.APP_API_URL,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    Version: '2025_R09',
    'X-User-Agent': `myKubotaWeb/${version} - ${oauthClientId}`,
  },
};

export const axiosInstance = axios.create(commonConfig);
export const anonymousAxiosInstance = axios.create(commonConfig);

[axiosInstance, anonymousAxiosInstance].forEach(instance => {
  instance.interceptors.request.use(
    config => {
      config.headers = {
        ...config.headers,
        'Accept-Language': getLocale(),
      };
      return config;
    },
    error => Promise.reject(error),
  );
});

export const setupAxiosAuthInterceptors = (authService: AuthService) => {
  axiosInstance.interceptors.request.use(
    async config => {
      if (config.url?.includes('/oauth/token')) {
        return config;
      }

      const token = await authService.getAccessToken();

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      return config;
    },
    error => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !(originalRequest as any)._retry
      ) {
        try {
          const canRetry = await authService.handleUnauthorizedError(error);
          if (canRetry) {
            return axiosInstance(originalRequest);
          }
        } catch (e) {
          if (e instanceof TokenRefreshError) {
            authService.logout();
          }
        }
      }

      return Promise.reject(error);
    },
  );
};
